import { Stack, styled as toolkitStyled } from '@ethings/ui-toolkit';
import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { unstable_styleFunctionSx as unstableStyleFunctionSx } from '@mui/system';
import { FC, ReactNode } from 'react';
import { Icon } from '../icons';

interface MapLayerToggleProps {
  type: string;
  label: string;
  thumbnail: string | ReactNode;
  checked: boolean;
  onChange: (type: string, checked: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
  sx?: SxProps<Theme>;
  'data-testid'?: string;
}

const ThumbnailWrapper = styled(Box)({
  position: 'relative',
  display: 'inline-block',
});

const ThumbnailContainer = styled('button', {
  shouldForwardProp: (prop) => prop !== 'checked',
})<{ checked: boolean }>(({ theme, checked }) => ({
  width: 70,
  height: 70,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  cursor: 'pointer',
  border: `2px solid ${checked ? theme.palette.primary.main : theme.palette.grey[300]}`,
  position: 'relative',
  transition: 'all 0.2s ease',
  padding: 0,
  background: 'none',
  '&:hover:not(:disabled)': {
    borderColor: checked ? theme.palette.primary.main : theme.palette.grey[400],
  },
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

const CheckmarkOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: -6,
  right: -6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '50%',
  width: 22,
  height: 22,
  color: theme.palette.background.paper,
  pointerEvents: 'none',
}));

const ToggleStack = toolkitStyled(Stack)<{ disabled: boolean; mergedSx?: SxProps<Theme> }>(({ disabled, mergedSx, theme }) => ({
  opacity: disabled ? 0.5 : 1,
  ...(unstableStyleFunctionSx({ sx: mergedSx, theme } as never) as Record<string, unknown>),
}));

export const MapLayerToggle: FC<MapLayerToggleProps> = ({ type, label, thumbnail, checked, onChange, disabled = false, tooltip, sx, ...props }) => {
  const handleClick = (): void => {
    if (!disabled) {
      onChange(type, !checked);
    }
  };

  return (
    <ToggleStack spacing={1} alignItems="center" disabled={disabled} mergedSx={sx}>
      <Tooltip title={disabled && tooltip ? tooltip : ''} placement="bottom" arrow disableHoverListener={!disabled || !tooltip}
        componentsProps={{
          tooltip: {
            sx: { maxWidth: 120 },
          },
        }}>
        <ThumbnailWrapper>
          <ThumbnailContainer
            type="button"
            checked={checked}
            disabled={disabled}
            onClick={handleClick}
            data-testid={props['data-testid'] ?? `${type}-thumbnail`}
            aria-pressed={checked}
            aria-label={`${label} layer ${checked ? 'enabled' : 'disabled'}`}
          >
            {typeof thumbnail === 'string' ? <img src={thumbnail} alt={label} /> : thumbnail}
          </ThumbnailContainer>

          {checked && (
            <CheckmarkOverlay data-testid="thumbnail-checkmark">
              <Icon icon="Check" style={{ fontSize: 14 }} />
            </CheckmarkOverlay>
          )}
        </ThumbnailWrapper>
      </Tooltip>
      {label && (
        <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 70 }}>
          {label}
        </Typography>
      )}
    </ToggleStack>
  );
};
